import { createRouter, createWebHistory, RouteRecordRaw } from "vue-router";
import { onAuthStateChanged } from "firebase/auth";
import { doc, onSnapshot } from "firebase/firestore";
import { auth, firestore } from "./firebase";
import { isOperator } from "@shared/entities/System/OperatorAllowlist";

import AuthLayout from "./layout/AuthLayout.vue";
import DashboardLayout from "./layout/DashboardLayout.vue";
import * as Views from "./views";

import dev from "./components/dev/devComponent.vue";
import * as Components from "./components";
import { store } from "@/store";

const routes: Array<RouteRecordRaw> = [
  {
    path: "",
    name: "frontpage",
    component: Components.FrontPage,
  },
  {
    path: "/",
    component: DashboardLayout,
    children: [
      {
        path: "/home",
        name: "home",
        component: Views.Home, //() => import("Views.Home.vue"),
        meta: {
          requiresAuth: true,
          accessKey: "home",
          label: "Inicio",
          icon: "fas fa-home fa-fw",
        },
      },
      {
        path: "tickets",
        name: "tickets",
        component: Views.TicketPacks, //() => import("Views.TicketPacks.vue"),
        meta: {
          requiresAuth: true,
          accessKey: "tickets",
          label: "Mis Boletos",
          icon: "fas fa-ticket-alt fa-fw",
        },
      },
      {
        path: "subscriptions",
        name: "subscriptions",
        component: Views.Subscriptions, //() => import("Views.Subscriptions.vue"),
        meta: {
          requiresAuth: true,
          accessKey: "subscriptions",
          label: "Suscripciones",
          icon: "fas fa-store fa-fw",
        },
      },
      {
        path: "academy",
        name: "academy",
        component: Views.Academy, //() => import("Views.Withdrawals.vue"),
        meta: {
          requiresAuth: true,
          accessKey: "academy",
          label: "Academia Virtual",
          icon: "fas fa-chalkboard-teacher fa-fw",
        },
      },
      {
        path: "recorded-classes",
        name: "rec-classes",
        component: Views.RecordedClasses, //() => import("Views.Withdrawals.vue"),
        meta: {
          requiresAuth: true,
          accessKey: "rec-classes",
          label: "Clases Grabadas",
          icon: "fas fa-video fa-fw",
        },
      },
      {
        path: "gtr-system",
        name: "gtr-system",
        component: Views.GtrSystem, //() => import("Views.Withdrawals.vue"),
        meta: {
          requiresAuth: true,
          accessKey: "gtr-system",
          label: "Sistema GTR Evolution",
          icon: "fas fa-brain fa-fw",
        },
      },
      {
        // Ruta PLANA. Antes eran dos rutas anidadas: un padre
        // `mastermind-intensive` (un contenedor vacío con su `<router-view>`) y
        // una hija `colombia`. El capítulo desaparece de la URL porque sólo hay
        // uno y nunca se eligió desde aquí; el capítulo sigue vivo en los DATOS
        // (`mmiTree/colombia`, el store `mmi`), no en la dirección.
        path: "acelerador",
        name: "acelerador",
        component: Components.MMI.Colombia,
        meta: {
          requiresAuth: true,
          // El `accessKey` NO es el nombre de la ruta ni la clave del menú: es
          // la llave del PERMISO, y el guard de más abajo la compara contra
          // `userPermissions` (que sale de los perfiles de `permissions/*`).
          // Se queda en `mmi-colombia` — cambiarla dejaría la pantalla
          // inaccesible a quien sí compró el producto.
          accessKey: "mmi-colombia",
          // Etiqueta e icono de RESPALDO: el título que se ve de verdad sale
          // del doc `menu/acelerador` de Firestore (ver `stores/menu.ts`,
          // `getMenuItemForRoute`), que empareja por `Key === nombre de la
          // ruta`. Si ese doc no existe, o su `Key` no casa con `acelerador`,
          // se cae aquí en silencio y sin ningún error.
          label: "Acelerador de Riqueza",
          icon: "fas fa-fire fa-fw",
        },
      },
      {
        path: "commissions",
        name: "commissions",
        component: Views.Commissions, //() => import("Views.Commissions.vue"),
        meta: {
          requiresAuth: true,
          accessKey: "commissions",
          label: "Comisiones",
          icon: "fas fa-wallet fa-fw",
        },
      },
      {
        path: "gtr-points",
        name: "gtr-points",
        component: Views.GtrPoints, //() => import("Views.GtrPoints.vue"),
        meta: {
          requiresAuth: true,
          accessKey: "gtr-points",
          label: "GTR Points",
          icon: "fas fa-coins fa-fw",
        },
      },
      {
        path: "withdrawals",
        name: "withdrawals",
        component: Views.Withdrawals, //() => import("Views.Withdrawals.vue"),
        meta: {
          requiresAuth: true,
          accessKey: "withdrawals",
          label: "Retiros",
          icon: "fab fa-bitcoin fa-fw",
        },
      },
      {
        path: "payments",
        name: "payments",
        component: Views.Payments, //() => import("Views.Payments.vue"),
        meta: {
          requiresAuth: true,
          accessKey: "payments",
          label: "Pagos Realizados",
          icon: "fas fa-credit-card fa-fw",
        },
      },
      {
        path: "tools",
        name: "tools",
        component: Views.Tools, //() => import("Views.Payments.vue"),
        meta: {
          requiresAuth: true,
          accessKey: "tools",
          label: "Herramientas",
          icon: "fas fa-tools fa-fw",
        },
      },
      {
        path: "qualification-dashboard",
        name: "qualification-dashboard",
        component: Views.QualificationDashboard,
        meta: {
          requiresAuth: true,
          accessKey: "qualification-dashboard",
          label: "Tablero de Calificación",
          icon: "fas fa-tachometer-alt fa-fw",
        },
      },
      {
        path: "my-team",
        name: "my-team",
        component: Views.MyTeam,
        meta: {
          requiresAuth: true,
          accessKey: "my-team",
          label: "Mi Equipo",
          icon: "fas fa-users fa-fw",
        },
      },
      {
        path: "unilevel",
        name: "unilevel",
        component: Views.Unilevel, //() => import ("Views.Unilevel.vue"),
        meta: {
          requiresAuth: true,
          accessKey: "unilevel",
          label: "Unilevel",
          icon: "fas fa-sitemap fa-fw",
        },
      },
      {
        // ALIAS DEL ÁRBOL VIEJO. La pantalla vieja del binario
        // (`views/BinaryNetwork.vue`, de 2023) se jubila: se retira del menú
        // borrando el doc `menu/binary-network`, y su ruta se queda viva
        // apuntando a la Vista de Árbol nueva, para que los marcadores que la
        // gente tenga guardada sigan resolviendo.
        //
        // Redirigir no pierde nada: ninguna de las dos rutas lee `$route.query`
        // ni `$route.params`, así que un marcador de `/binary-network` es la URL
        // desnuda. Las dos arrancan en el usuario logueado y las dos leen los
        // mismos datos (`Users-getBinaryTree`, `Users-getBinaryTreeBatch`,
        // `Users-searchUser`).
        //
        // El `name` se conserva aunque hoy nadie navegue por él: es gratis y
        // evita que un `router.push({ name: "binary-network" })` futuro reviente.
        //
        // OJO: con un `redirect`, el guard de más abajo evalúa el `accessKey` de
        // la ruta DESTINO, no el de aquí. Por eso este record ya no declara
        // ninguno — el permiso que cuenta es el de `binary-tree`.
        path: "binary-network",
        name: "binary-network",
        redirect: { name: "binary-tree" },
      },
      {
        path: "binary-tree",
        name: "binary-tree",
        component: Views.BinaryTreeView,
        meta: {
          requiresAuth: true,
          // DOS LLAVES A PROPÓSITO, no es un duplicado. El guard acepta arrays
          // (unas líneas más abajo: `accessKey.some(...)`), y basta con tener
          // UNA de las dos.
          //
          // `binary-tree` es la llave propia de esta pantalla. `binary-network`
          // es la de la pantalla vieja que ahora redirige aquí: sin ella, quien
          // llegue por un marcador de `/binary-network` y todavía no tenga el
          // permiso nuevo acabaría en «Denegado» en vez de en el árbol. Es la
          // red de seguridad del paso de datos que reparte `binary-tree` a los
          // perfiles `distributorLicense`, `master` y `root`.
          //
          // CUÁNDO SE PUEDE QUITAR `binary-network` DE ESTA LISTA: cuando ningún
          // documento de `permissions` lo lleve ya en su array `data`. Mientras
          // alguien lo tenga, quitarlo le cierra la puerta a su marcador. No lo
          // borres por parecer redundante — no lo es.
          accessKey: ["binary-tree", "binary-network"],
          label: "Red Binaria",
          icon: "fas fa-share-alt fa-fw fa-rotate-90",
        },
      },
      {
        path: "profile",
        name: "profile",
        component: Views.Profile, //() => import ("Views.Profile.vue"),
        meta: {
          requiresAuth: true,
          accessKey: "profile",
          label: "Mi Perfil",
          icon: "fas fa-user fa-fw",
        },
      },
      {
        path: "queries",
        name: "queries",
        component: Views.Queries, //() => import ("Views.Queries.vue"),
        meta: {
          requiresAuth: true,
          accessKey: "queries",
          label: "Consultas",
          icon: "fas fa-list-alt fa-fw",
        },
      },
      {
        path: "channels",
        name: "channels",
        component: Views.Channels, //() => import ("Views.Channels.vue"),
        meta: {
          requiresAuth: true,
          accessKey: "channels",
          label: "Canales",
          icon: "fas fa-comments fa-fw",
        },
      },
      {
        path: "channels/:id/users",
        name: "channel-users",
        component: () => import("./views/ChannelUserManagement.vue"),
        meta: {
          requiresAuth: true,
          accessKey: "channels", // Shares permission with channels
          label: "Usuarios del Canal",
          icon: "fas fa-users fa-fw",
        },
      },
      {
        path: "alerts",
        name: "alerts",
        component: Views.Alerts, //() => import ("Views.Alerts.vue"),
        meta: {
          requiresAuth: true,
          accessKey: "alerts",
          label: "Alertas",
          icon: "fas fa-broadcast-tower fa-fw",
        },
      },
      {
        path: "sys-config",
        name: "sys-config",
        component: Views.SysConfig,
        redirect: "/sys-config/users-tools",
        meta: {
          requiresAuth: true,
          accessKey: "sys-config",
          label: "System Configuration",
          icon: "fas fa-cogs fa-fw",
        },
        children: [
          // {
          //   path: "payouts",
          //   name: "Payouts",
          //   component: Components.SysConfig.Payouts,
          //   meta: {}
          // },
          {
            path: "users-tools",
            name: "UserTools",
            component: Components.SysConfig.UsersTools,
            meta: {
              label: "Herramientas de Usuario",
              icon: "fas fa-tools fa-fw",
            },
          },
          {
            // The migration console (#556). This route only decides what RENDERS —
            // the door is server-side: every Migration-* callable validates the
            // operator allowlist itself, so reaching this page buys you nothing.
            path: "migration",
            name: "MigrationConsole",
            component: Components.SysConfig.MigrationConsole,
            meta: {
              label: "Consola de Migración",
              icon: "fas fa-diagram-project fa-fw",
            },
          },
          {
            path: "schedules-configuration",
            name: "SchedulesConfiguration",
            component: Components.SysConfig.SchedulesConfiguration,
            meta: {
              label: "Configuración de Horarios",
              icon: "fas fa-clock fa-fw",
            },
          },
          {
            path: "profile-permissions",
            name: "ProfilePermissionsConfiguration",
            component: Components.SysConfig.ProfilePermissionsConfiguration,
            meta: {
              label: "Permisos de Perfil",
              icon: "fas fa-user-shield fa-fw",
            },
          },
          {
            path: "user-profiles",
            name: "UserProfilesConfiguration",
            component: Components.SysConfig.UserProfilesConfiguration,
            meta: {
              label: "Perfiles de Usuario",
              icon: "fas fa-users-cog fa-fw",
            },
          },
        ],
      },
      {
        path: "payouts",
        name: "payouts",
        component: Views.Payouts,
        meta: {
          requiresAuth: true,
          accessKey: "payouts",
          label: "Dispersión de Pagos",
          icon: "fas fa-money-check-alt fa-fw",
        },
      },
      {
        path: "vault",
        name: "vault",
        component: Views.VaultManagement,
        redirect: "/vault/transactions",
        meta: {
          requiresAuth: true,
          accessKey: "vault",
          label: "Bóveda",
          icon: "fas fa-vault fa-fw",
        },
        children: [
          {
            path: "transactions",
            name: "transactions",
            component: Components.Vault.VaultTransactions,
            meta: {
              label: "Transacciones",
              icon: "fas fa-random fa-fw",
            },
          },
          {
            path: "system-withdrawals",
            name: "system-withdrawals",
            component: Components.Vault.SentWithdrawals,
            meta: {
              label: "Retiros del Sistema",
              icon: "fas fa-sign-out-alt fa-fw",
            },
          },
          {
            path: "pending-withdrawals",
            name: "pending-withdrawals",
            component: Components.Vault.PendingWithdrawals,
            meta: {
              accessKey: "withdrawalRequestApprover",
              label: "Retiros Pendientes",
              icon: "fas fa-exclamation-circle fa-fw",
            },
          },
          {
            path: "addresses",
            name: "addresses",
            component: Components.Vault.VaultAddresses,
            meta: {
              label: "Direcciones",
              icon: "fas fa-qrcode fa-fw",
            },
          },
          {
            path: "create-withdrawal",
            name: "create-withdrawal",
            component: Components.Vault.CreateWithdrawal,
            meta: {
              accessKey: "createWithdrawalRequest",
              label: "Crear Retiro",
              icon: "fas fa-plus-circle fa-fw",
            },
          },
          {
            path: "address-book",
            name: "address-book",
            component: Components.Vault.AddressBook,
            meta: {
              accessKey: "createWithdrawalRequest",
              label: "Libreta de Direcciones",
              icon: "fas fa-address-book fa-fw",
            },
          },
        ],
      },
      {
        path: "/buy-tickets",
        name: "buy-tickets",
        component: Views.BuyTickets,
        meta: {
          requiresAuth: false,
          accessKey: "buy-tickets",
          label: "Comprar Boletos",
          icon: "far fa-calendar-check fa-fw",
        },
      },
      {
        path: "/buy-tickets/checkout/:id",
        name: "buy-tickets-checkout",
        component: Views.BuyTicketsCheckout,
        meta: {
          requiresAuth: false,
          accessKey: "buy-tickets-checkout",
          label: "Finalizar Compra",
          icon: "far fa-calendar-check fa-fw",
        },
      },
      {
        path: "events/set",
        name: "set-tickets-admin",
        component: Views.SetTicketsAdmin,
        meta: {
          requiresAuth: true,
          accessKey: "set-tickets-admin",
          label: "Asignar Boletos",
          icon: "fas fa-ticket-alt fa-fw",
        },
      },
      {
        path: "events",
        name: "buy-tickets-admin",
        component: Views.BuyTicketsAdmin,
        meta: {
          requiresAuth: true,
          accessKey: "buy-tickets-admin",
          label: "Configurar Eventos",
          icon: "fas fa-ticket-alt fa-fw",
        },
      },
      {
        path: "events/new",
        name: "buy-tickets-admin-new-event",
        component: Views.BuyTicketsNewEvent,
        meta: {
          requiresAuth: true,
          accessKey: "buy-tickets-admin-new-event",
          label: "Nuevo Evento",
          icon: "fas fa-calendar-plus fa-fw",
        },
      },
      {
        path: "events/archive",
        name: "buy-tickets-admin-archive",
        component: Views.BuyTicketsAdminArchive,
        meta: {
          requiresAuth: true,
          accessKey: "buy-tickets-admin-archive",
          label: "Eventos Archivados",
          icon: "fas fa-box-archive fa-fw",
        },
      },
      {
        path: "events/edit/:id",
        name: "buy-tickets-admin-edit-event",
        props: true,
        component: Views.BuyTicketsEditEvent,
        meta: {
          requiresAuth: false,
          accessKey: "buy-tickets-admin-edit-event",
          label: "Editar Evento",
          icon: "fas fa-calendar-pen fa-fw",
        },
      },
    ],
  },
  {
    path: "/",
    component: AuthLayout,
    children: [
      {
        path: "/login",
        name: "login",
        component: Views.Login,
      },
      {
        // Unlisted on purpose (#560): the way an allowlisted operator reaches
        // login during a freeze, when everyone else - including a socio just
        // trying `/login` - is bounced to `/offline` without a hint that this
        // path exists. This is obscurity, not a security boundary; the real
        // gate is server-side (`withFreezeGuard`, functions/src/System/FreezeGuard.ts).
        //
        // SÓLO HABILITADA DURANTE EL CONGELADO. El record existe siempre porque
        // el array de rutas se evalúa al importar el módulo, cuando todavía no
        // se sabe si hay congelado; quien lo decide es el `beforeEach` de más
        // abajo, que ya esperó al snapshot del flag. Fuera del congelado manda a
        // `/login`, que renderiza este mismo componente.
        path: "/admin-login",
        name: "admin-login",
        component: Views.Login,
      },
      {
        path: "/registro",
        name: "register",
        component: Views.Register,
        props: true,
        children: [
          {
            path: ":username/:side",
            name: "registration",
            component: Views.Register,
            props: true,
          },
        ],
      },
      {
        path: "/reset-password/:token",
        name: "resetPassword",
        component: Views.ResetPassword,
        props: true,
      },
    ],
  },
  {
    path: "/ticket-code/:token",
    name: "",
    component: Views.QRCodeGenerate,
    props: true,
  },
  // {
  //   path: "/about",
  //   name: "about",
  //   component: About
  // },
  {
    path: "/offline",
    name: "offline",
    component: Components.UpdatingSystem,
  },
  {
    path: "/dev",
    name: "dev",
    component: dev,
  },
  {
    path: "/denied",
    name: "denied",
    component: Views.Denied,
  },
  {
    path: "/:pathMatch(.*)*",
    name: "not-found",
    component: Views.NotFound,
  },
];

const router = createRouter({
  history: createWebHistory(process.env.BASE_URL),
  linkExactActiveClass: "active",
  routes: routes,
});

// `auth.currentUser` is read synchronously, but Firebase Auth resolves the
// persisted session asynchronously. On a hard refresh the very first
// `beforeEach` can run before that resolution lands, so `currentUser` is
// still null even for an operator with a valid session - which would bounce
// them to `/offline` mid-session (#560 follow-up). Cache the wait on the
// first `onAuthStateChanged` firing (it always fires once auth state is
// known, user or not) so every navigation after the first just awaits an
// already-resolved promise - no extra listener per navigation.
//
// Use the MODULAR `auth` here, not `authCompat`: `AuthStore.ts`'s login
// action signs in through the modular SDK, which updates `auth.currentUser`
// synchronously on success - `authCompat.currentUser` is a compat mirror
// that lags one listener tick behind, which raced the immediate
// `router.push({ name: "home" })` right after a fresh login and bounced a
// just-authenticated operator straight back to the login form.
//
// Restoring `auth.currentUser` is only half the story: everything `login()`
// loads for a fresh sign-in - permissions, profile (nickname/picture/roles,
// via `doGetUserProfile`'s Firestore listener), email-verified status, user
// flags - is ONLY ever dispatched from that one action. Nothing in this app
// re-loads any of it for an already-authenticated session on a fresh page
// load (no `vuex-persistedstate` either, see store/index.ts). So on every
// hard refresh, permissions come back empty regardless of who's logged in,
// the guard's `getUserPermissions().length <= 0` branch sends everyone to
// `/login`, and `Login.vue`'s `mounted()` hook calls `auth/logout`
// unconditionally - which signs the operator OUT for real. That combination
// is the actual mechanism behind "operador recarga la consola durante el
// freeze y lo patea": it isn't specific to the freeze at all, it happens on
// every reload. Fixed at the root, in two places:
//   1. `AuthStore.ts` factors the post-authentication bootstrap out of
//      `login()` into `restoreSession(user)`, so there's one implementation
//      to keep in sync, not two.
//   2. Here: if a session is already restored (the first
//      `onAuthStateChanged` firing reports a user), run that SAME bootstrap
//      for them before the guard ever runs - so a refresh ends up with
//      exactly the state a real login would have produced, not just enough
//      to slip past the permissions check.
let sessionReadyPromise: Promise<void> | null = null;
function waitForAuthReady(): Promise<void> {
  if (!sessionReadyPromise) {
    sessionReadyPromise = new Promise((resolve) => {
      const unsubscribe = onAuthStateChanged(auth, async (user) => {
        unsubscribe();
        if (user?.displayName) {
          // The whole point of #572 was that a refresh with a valid session
          // must NOT kick you out. But `auth.currentUser` being set doesn't
          // mean the session is usable: the guard sends you to /login (which
          // logs you out) when permissions are empty, and permissions live
          // behind restoreSession. So a TRANSIENT failure here (a Firestore
          // hiccup, a cold `getUserPermissions` call) leaves auth.currentUser
          // set but permissions empty -> the guard can't tell "not logged in"
          // from "bootstrap failed" and kicks a valid session out. That IS the
          // bug this PR fixed, re-entering through the failure path.
          //
          // So: retry a couple of times before giving up. A permanent failure
          // still falls through (permissions stay empty -> kickout -> re-auth,
          // the correct outcome when the session genuinely can't be restored);
          // a transient one is absorbed. We never block navigation - the loop
          // is bounded and each attempt is awaited, not raced.
          const MAX_ATTEMPTS = 3;
          for (let attempt = 1; attempt <= MAX_ATTEMPTS; attempt++) {
            try {
              await store.dispatch("auth/restoreSession", user);
              break;
            } catch (e) {
              console.error(
                `restoreSession failed on auth-ready (attempt ${attempt}/${MAX_ATTEMPTS}):`,
                e
              );
            }
          }
        }
        resolve();
      });
    });
  }
  return sessionReadyPromise;
}

// The freeze redirect below reads the `offline` flag. The store's own copy
// (`sys/isOffline`) is populated by `sys/doGetSystemConfig`, dispatched from
// `App.vue::created()` - which fires AFTER the first `beforeEach`, and via an
// unawaited `onSnapshot`. So on the very first navigation (a deep-link to
// `/login` during a freeze) that flag is still its default `false`, and a socio
// would see the login form with no later navigation to bounce them - defeating
// the obfuscation (#560). So the guard reads its OWN copy, seeded from a
// snapshot we wait for before evaluating, exactly like `waitForAuthReady`.
let offlineReadyPromise: Promise<void> | null = null;
let offlineFlag = false;
function waitForOfflineReady(): Promise<void> {
  if (!offlineReadyPromise) {
    offlineReadyPromise = new Promise((resolve) => {
      let settled = false;
      const done = () => {
        if (!settled) {
          settled = true;
          resolve();
        }
      };
      onSnapshot(
        doc(firestore, "systemConfiguration/frontend"),
        (snap) => {
          offlineFlag = snap.data()?.offline === true;
          done();
        },
        (err) => {
          // Fail closed: if we can't read the flag, assume frozen so we never
          // flash the login form during a freeze we couldn't confirm was off.
          // eslint-disable-next-line no-console
          console.error("systemConfiguration/frontend snapshot error:", err);
          offlineFlag = true;
          done();
        }
      );
    });
  }
  return offlineReadyPromise;
}

router.beforeEach(async (to, from, next) => {
  await waitForAuthReady();
  await waitForOfflineReady();
  const requiresAuth = to.matched.some((x) => x.meta.requiresAuth);
  const accessKey = [...to.matched]
    .reverse()
    .find((record) => record.meta && record.meta.accessKey !== undefined)
    ?.meta.accessKey;
  const userPermissions = store.getters["auth/getUserPermissions"];
  const hasPermission = (() => {
    if (accessKey === undefined) return true;
    if (!userPermissions?.includes) return false;
    if (Array.isArray(accessKey)) {
      return accessKey.some((key) => userPermissions.includes(key));
    }
    return userPermissions.includes(accessKey);
  })();
  // Router-local flag, seeded by `waitForOfflineReady` above - reliable on the
  // first navigation, unlike the store getter (see waitForOfflineReady). Both
  // track the same doc; the store copy still exists for other components.
  const offline = offlineFlag;
  // The freeze locks out everyone EXCEPT allowlisted operators, who still
  // need the console to validate the new trees (Vista de Árbol) before
  // opening the gate back up. This is a client-side convenience only - the
  // real gate lives server-side in `withFreezeGuard` (functions/src/System/FreezeGuard.ts),
  // since this check is trivially bypassed from the browser console (#560).
  const currentUsername = auth.currentUser?.displayName;
  const bypassesOffline = offline && isOperator(currentUsername);
  // A socio who navigates to `/login` during a freeze must see the offline
  // page, not the login form - Hector doesn't want it obvious that another
  // door exists. `/admin-login` renders the exact same `Login` component but
  // is unlisted (no nav/menu link) and exempt from the redirect, same as
  // `/offline` itself - it's the door only the allowlisted operators know
  // to type. This is obscurity, not the security boundary; that one is
  // server-side (`withFreezeGuard`).
  const isAdminLoginRoute = to.name === "admin-login";
  // console.debug("-----------");
  // console.debug("auth.currentUser", auth.currentUser?.displayName);
  // console.debug("from", from);
  // console.debug("to", to);
  // console.debug(store.getters["auth/getUserPermissions"].length);
  // console.debug("requiresAuth", requiresAuth);
  // console.debug("hasPermission", hasPermission);
  // console.debug("to.meta.accessKey", to.meta.accessKey);

  if (isAdminLoginRoute && auth.currentUser && userPermissions.length > 0) {
    // `Login.vue`'s `mounted()` dispatches `auth/logout` unconditionally (an
    // old multi-tab sync behavior, April 2021). So merely *reaching* this
    // route destroys a valid session. On `/login` that's harmless - you went
    // there to sign in anyway - but an operator who is already signed in and
    // types `/admin-login` mid-migration would silently log themselves out.
    // Send them to the console instead; signing out stays an explicit action
    // via the profile menu.
    next("/home");
  } else if (isAdminLoginRoute && !offline) {
    // La puerta del operador SÓLO existe durante el congelado. Fuera de él es
    // una puerta duplicada: `/login` renderiza el mismo componente y funciona
    // igual, así que no aporta nada y sí superficie.
    //
    // POR QUÉ ESTO NO PUEDE DEJAR A NADIE FUERA, y por qué no hay que revertirlo
    // «por si acaso»: si esta condición se equivocara y la app creyera que NO
    // hay congelado habiéndolo, el operador entra por `/login`, que sigue ahí.
    // Y si hay congelado de verdad, la condición es falsa y la puerta está
    // abierta. En ningún escenario se cierra el paso — esta puerta sólo importa
    // exactamente cuando la condición es cierta.
    //
    // Va DETRÁS de la rama de arriba a propósito: un operador ya logueado tiene
    // que seguir yendo a `/home`, no aquí. `Login.vue` hace `logout` al
    // montarse, así que mandarlo a `/login` le cerraría la sesión sin querer.
    next("/login");
  } else if (
    offline &&
    to.name != "offline" &&
    !isAdminLoginRoute &&
    !bypassesOffline
  ) {
    next("/offline");
  } else if (!offline && to.name == "offline") {
    next("/login");
  } else if (requiresAuth && !auth.currentUser) {
    next("/login");
  } else if (
    requiresAuth &&
    store.getters["auth/getUserPermissions"].length <= 0
  ) {
    next("/login");
  } else if (hasPermission || accessKey == undefined) {
    next();
  } else {
    next("/denied");
  }
});

export default router;
